import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Save,
  Share2,
  Trash2,
  Activity,
  Droplets,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Info,
  Zap,
  Dumbbell,
  Scale,
  Heart,
  Cloud,
  UserPlus
} from 'lucide-react';
import {
  formatBMI,
  formatWeight,
  getWHOCategory,
  getAthletePerformanceBand,
  getWaistToHeightRisk,
  shouldShowLeanMassBias,
  calculateBMIPrime,
  calculatePonderalIndex,
  calculateHealthyWeightRange,
  getHealthyBMIRange,
  estimatePaceImpact,
  estimateHydrationNeeds,
  getBMIScalePosition,
} from '@/lib/bmi-utils';
import { saveRecord } from '@/lib/storage';
import { toast } from 'sonner';

const IconMap = {
  AlertTriangle,
  Zap,
  Activity,
  Dumbbell,
  Scale
};

export const BMIResults = ({ results, onSave, saveLabel, saveDisabled, isAuthenticated = false, showSignupPrompt = true }) => {
  const navigate = useNavigate();
  const [weightChange, setWeightChange] = useState([0]);
  const [temperature, setTemperature] = useState('warm');
  const [saved, setSaved] = useState(false);

  if (!results) return null;

  const { bmi, weight, height, age, gender, athleteMode, waist, waistToHeightRatio, unit } = results;

  // Calculate all metrics
  const whoCategory = getWHOCategory(bmi);
  const athleteBand = getAthletePerformanceBand(bmi);
  const waistRisk = waistToHeightRatio ? getWaistToHeightRisk(waistToHeightRatio) : null;
  const showLeanMassBias = shouldShowLeanMassBias(bmi, waistToHeightRatio);
  const bmiPrime = calculateBMIPrime(bmi);
  const ponderalIndex = calculatePonderalIndex(weight, height);
  const healthyWeightRange = calculateHealthyWeightRange(height);
  const healthyBMIRange = getHealthyBMIRange();
  const scalePosition = getBMIScalePosition(bmi);
  const paceImpact = estimatePaceImpact(weightChange[0]);
  const hydration = estimateHydrationNeeds(weight, temperature);

  const AthleteIcon = IconMap[athleteBand.icon] || Activity;

  // Add WHO category to results for saving
  results.whoCategory = whoCategory.label;
  results.athleteBand = athleteMode ? athleteBand.label : null;

  const handleSave = () => {
    // If onSave is provided (cloud save from dashboard), use it
    if (onSave) {
      onSave();
      return;
    }
    
    // Otherwise, save to localStorage
    const record = saveRecord({
      bmi: parseFloat(formatBMI(bmi)),
      weight,
      height,
      age,
      gender,
      athleteMode,
      waist,
      waistToHeightRatio,
      whoCategory: whoCategory.label,
      athleteBand: athleteMode ? athleteBand.label : null
    });

    if (record) {
      toast.success('Result saved to browser history!');
      setSaved(true);
    } else {
      toast.error('Failed to save result');
    }
  };

  const handleShare = async () => {
    const summary = `My BMI Results:
• BMI: ${formatBMI(bmi)} (${whoCategory.label})
• Weight: ${formatWeight(weight, unit)}
• BMI Prime: ${bmiPrime.toFixed(2)}
${athleteMode ? `• Athlete Zone: ${athleteBand.label}` : ''}
${waistToHeightRatio ? `• Waist-to-Height: ${waistToHeightRatio.toFixed(2)}` : ''}

Calculated with BMI Calc Hub - Athlete Edition`;

    try {
      await navigator.clipboard.writeText(summary);
      toast.success('Results copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy results');
    }
  };

  const getCategoryBadgeClass = (color) => {
    const classes = {
      info: 'badge-underweight',
      success: 'badge-normal',
      warning: 'badge-overweight',
      destructive: 'badge-obese'
    };
    return classes[color] || 'badge-normal';
  };

  return (
    <div className="space-y-4 animate-slide-up">
      {/* Main BMI Result Card */}
      <Card className="card-elevated overflow-hidden">
        <CardHeader className="bg-primary/5 border-b border-border pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Your BMI Results
            </CardTitle>
            <div className="flex gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size={saveLabel ? "sm" : "icon"} 
                      onClick={handleSave} 
                      disabled={saveDisabled}
                      className={saveLabel ? "h-9" : "h-9 w-9"}
                    >
                      <Save className="w-4 h-4" />
                      {saveLabel && <span className="ml-2">{saveLabel}</span>}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{saveLabel || "Save to history"}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="outline" size="icon" onClick={handleShare} className="h-9 w-9">
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Copy results</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* BMI Value Display */}
          <div className="text-center mb-6">
            <div className="inline-flex items-baseline gap-2 mb-2">
              <span className="text-6xl font-bold gradient-text animate-number-pop">
                {formatBMI(bmi)}
              </span>
              <span className="text-2xl text-muted-foreground">BMI</span>
            </div>
            <Badge className={`${getCategoryBadgeClass(whoCategory.color)} text-sm px-3 py-1`}>
              {whoCategory.label}
            </Badge>
          </div>

          {/* BMI Scale */}
          <div className="mb-6">
            <div className="relative h-4 rounded-full bmi-scale-gradient overflow-hidden">
              <div
                className="absolute top-1/2 -translate-y-1/2 w-4 h-6 bg-foreground rounded-sm border-2 border-background shadow-lg transition-all duration-500"
                style={{ left: `calc(${scalePosition}% - 8px)` }}
              />
            </div>
            {/* Scale labels positioned at correct percentages: 15=0%, 18.5=14%, 25=40%, 30=60%, 40=100% */}
            <div className="relative h-5 mt-2 text-xs text-muted-foreground">
              <span className="absolute left-0">15</span>
              <span className="absolute" style={{ left: '14%', transform: 'translateX(-50%)' }}>18.5</span>
              <span className="absolute" style={{ left: '40%', transform: 'translateX(-50%)' }}>25</span>
              <span className="absolute" style={{ left: '60%', transform: 'translateX(-50%)' }}>30</span>
              <span className="absolute right-0">40</span>
            </div>
            <div className="relative h-4 mt-1 text-xs">
              <span className="absolute left-0 text-info">Underweight</span>
              <span className="absolute text-success" style={{ left: '27%', transform: 'translateX(-50%)' }}>Normal</span>
              <span className="absolute text-warning" style={{ left: '50%', transform: 'translateX(-50%)' }}>Overweight</span>
              <span className="absolute right-0 text-destructive">Obese</span>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-muted/50 rounded-xl p-3 text-center cursor-help hover:bg-muted/70 transition-colors">
                    <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                      BMI Prime
                      <Info className="w-3 h-3" />
                    </p>
                    <p className="text-lg font-bold text-foreground">{bmiPrime.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {bmiPrime < 1 ? 'Below' : bmiPrime === 1 ? 'At' : 'Above'} upper normal
                    </p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="font-semibold mb-1">BMI Prime</p>
                  <p className="text-sm">Your BMI divided by 25 (upper limit of normal). A value of 1.0 means you're at the upper boundary of normal weight. Below 1.0 is normal, above 1.0 is overweight.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="bg-muted/50 rounded-xl p-3 text-center cursor-help hover:bg-muted/70 transition-colors">
                    <p className="text-xs text-muted-foreground mb-1 flex items-center justify-center gap-1">
                      Ponderal Index
                      <Info className="w-3 h-3" />
                    </p>
                    <p className="text-lg font-bold text-foreground">{ponderalIndex.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {ponderalIndex >= 11 && ponderalIndex <= 15 ? 'Normal range' : ponderalIndex < 11 ? 'Below normal' : 'Above normal'}
                    </p>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  <p className="font-semibold mb-1">Ponderal Index (Rohrer's Index)</p>
                  <p className="text-sm">Weight divided by height cubed (kg/m³). Unlike BMI, it accounts better for height differences. Normal range is 11-15. Often more accurate for very tall or short individuals.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <div className="bg-muted/50 rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Healthy BMI</p>
              <p className="text-lg font-bold text-foreground">{healthyBMIRange.min}-{healthyBMIRange.max}</p>
              <p className="text-xs text-muted-foreground mt-1">WHO standard</p>
            </div>
            <div className="bg-muted/50 rounded-xl p-3 text-center">
              <p className="text-xs text-muted-foreground mb-1">Healthy Weight</p>
              <p className="text-lg font-bold text-foreground">
                {healthyWeightRange.min.toFixed(0)}-{healthyWeightRange.max.toFixed(0)}kg
              </p>
              <p className="text-xs text-muted-foreground mt-1">For your height</p>
            </div>
          </div>

          {/* Waist-to-Height Ratio */}
          {waistRisk && (
            <div className={`p-4 rounded-xl mb-4 bg-${waistRisk.color}-light border border-${waistRisk.color}/20`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full bg-${waistRisk.color}/20 flex items-center justify-center flex-shrink-0`}>
                  {waistRisk.color === 'success' ? (
                    <CheckCircle className={`w-5 h-5 text-${waistRisk.color}`} />
                  ) : (
                    <AlertTriangle className={`w-5 h-5 text-${waistRisk.color}`} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-foreground">Waist-to-Height Ratio</p>
                    <Badge variant="outline" className="text-xs">
                      {waistToHeightRatio.toFixed(2)}
                    </Badge>
                  </div>
                  <p className={`text-sm font-medium text-${waistRisk.color}`}>{waistRisk.label}</p>
                  <p className="text-sm text-muted-foreground">{waistRisk.description}</p>
                </div>
              </div>
            </div>
          )}

          {/* Lean Mass Bias Warning */}
          {showLeanMassBias && (
            <div className="p-4 rounded-xl mb-4 bg-info-light border border-info/20">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-info/20 flex items-center justify-center flex-shrink-0">
                  <Info className="w-5 h-5 text-info" />
                </div>
                <div>
                  <p className="font-semibold text-foreground mb-1">Lean Mass Bias Detected</p>
                  <p className="text-sm text-muted-foreground">
                    Your BMI may overestimate body fat due to higher muscle mass. Your waist-to-height ratio suggests a healthy body composition despite elevated BMI.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Actions Card - Prominent save options */}
      {showSignupPrompt && !isAuthenticated && (
        <Card className="card-elevated border-primary/30 bg-gradient-to-r from-primary/5 to-accent/5">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  {saved ? (
                    <CheckCircle className="w-6 h-6 text-success" />
                  ) : (
                    <Save className="w-6 h-6 text-primary" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {saved ? 'Saved to Browser' : 'Save Your Results'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {saved 
                      ? 'Sign up to sync across devices and track progress over time'
                      : 'Keep track of your BMI history and monitor your progress'
                    }
                  </p>
                </div>
              </div>
              <div className="flex gap-3 w-full sm:w-auto">
                {!saved && (
                  <Button 
                    variant="outline" 
                    onClick={handleSave}
                    className="flex-1 sm:flex-initial"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Locally
                  </Button>
                )}
                <Button 
                  onClick={() => navigate('/register')}
                  className="flex-1 sm:flex-initial"
                >
                  <Cloud className="w-4 h-4 mr-2" />
                  {saved ? 'Sync to Cloud' : 'Save to Cloud'}
                </Button>
              </div>
            </div>
            {!saved && (
              <p className="text-xs text-muted-foreground mt-4 text-center sm:text-left">
                💡 Tip: Create a free account to save your history in the cloud and access it from any device
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Athlete Mode Results */}
      {athleteMode && (
        <Card className="card-elevated overflow-hidden border-primary/20">
          <CardHeader className="bg-primary/5 border-b border-border pb-4">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Athlete Performance Analysis
            </CardTitle>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Performance Band */}
            <div className={`p-4 rounded-xl bg-${athleteBand.color}-light border border-${athleteBand.color}/20`}>
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 rounded-full bg-${athleteBand.color}/20 flex items-center justify-center flex-shrink-0`}>
                  <AthleteIcon className={`w-6 h-6 text-${athleteBand.color}`} />
                </div>
                <div>
                  <p className={`font-bold text-lg text-${athleteBand.color}`}>{athleteBand.label}</p>
                  <p className="text-sm text-muted-foreground mt-1">{athleteBand.description}</p>
                </div>
              </div>
            </div>

            {/* Running Pace Impact Estimator */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <h4 className="font-semibold text-foreground">Running Pace Impact Estimator</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Simulate how weight changes might affect your running pace
              </p>
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Weight change</span>
                  <span className="font-bold text-foreground">
                    {weightChange[0] > 0 ? '+' : ''}{weightChange[0]} kg
                  </span>
                </div>
                <Slider
                  value={weightChange}
                  onValueChange={setWeightChange}
                  min={-10}
                  max={10}
                  step={0.5}
                  className="mb-4"
                />
                {weightChange[0] !== 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    {weightChange[0] < 0 ? (
                      <TrendingDown className="w-4 h-4 text-success" />
                    ) : (
                      <TrendingUp className="w-4 h-4 text-warning" />
                    )}
                    <span className="text-muted-foreground">Estimated impact:</span>
                    <span className={`font-semibold ${weightChange[0] < 0 ? 'text-success' : 'text-warning'}`}>
                      {paceImpact.minImpact.toFixed(0)}-{paceImpact.maxImpact.toFixed(0)} sec/km {paceImpact.direction}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Hydration Indicator */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Droplets className="w-5 h-5 text-info" />
                <h4 className="font-semibold text-foreground">Hydration Needs During Exercise</h4>
              </div>
              <div className="bg-muted/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-muted-foreground">Temperature</span>
                  <Select value={temperature} onValueChange={setTemperature}>
                    <SelectTrigger className="w-32 h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cool">Cool (&lt;15°C)</SelectItem>
                      <SelectItem value="warm">Warm (15-25°C)</SelectItem>
                      <SelectItem value="hot">Hot (&gt;25°C)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-center py-3">
                  <span className="text-3xl font-bold text-info">{hydration.recommended}</span>
                  <span className="text-lg text-muted-foreground ml-1">ml/hour</span>
                  <p className="text-sm text-muted-foreground mt-1">
                    Range: {hydration.min}-{hydration.max} ml/hour
                  </p>
                </div>
                {hydration.warning && (
                  <div className="mt-3 p-3 rounded-lg bg-warning-light border border-warning/20">
                    <p className="text-sm text-warning flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      {hydration.warning}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Disclaimer */}
            <div className="p-3 rounded-lg bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground text-center">
                <strong>Disclaimer:</strong> These athlete metrics are for informational purposes only and should not replace professional medical or sports performance advice.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BMIResults;
